var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { BackSide, Mesh, MeshBasicMaterial, SphereGeometry } from "three";
import { Quaternion, Vector3 } from "flavr-math";
import { degToRad, RoomChangedMessage, ROOT_MODEL_UID } from "flavr-common";
import { CreateMarkerMessage, DeleteMarkerMessage, EnableMarkerRadarMessage, SetClipFadeMessage, UpdateMarkerTargetMessage } from "@messages/rendering/MeshMessages";
import { ControllerConnectedMessage, ControllerDisconnectedMessage, ControllerMovedMessage } from "@messages/controller/ControllerMessages";
import { getControllerMeshInfo } from "@modules/inputs/controller/getControllerMeshInfo";
import { localPlayerContainer } from "@modules/localplayer/LocalPlayerContainer";
import { RenderOrder } from "@modules/rendering/RenderOrder";
import { mainScene } from "@modules/rendering/mainScene";
import { camera } from "@modules/rendering/camera";
import { modelPool } from "@modules/model";
import { MessageHandler, ModelEventHandler, ModelEventType, NewModule } from "@modules/module";
import { Marker, MarkerRoomInfo } from "./Marker";
const tmpVector31 = new Vector3();
/**
 * Displays room-aware world markers and an optional controller radar so navigation targets can stay visible in both desktop and XR views.
 *
 * Messages:
 * - `CreateMarkerMessage`, `DeleteMarkerMessage` and `UpdateMarkerTargetMessage` - Create, remove and retarget marker instances attached to models or rooms.
 * - `RoomChangedMessage`, `SetClipFadeMessage` and `EnableMarkerRadarMessage` - Update marker presentation for the active room and toggle radar-specific rendering.
 * - `ControllerConnectedMessage`, `ControllerDisconnectedMessage` and `ControllerMovedMessage` - Track controller poses that the radar visualization follows in XR.
 *
 * Events:
 * - `ModelEventType.MODEL_CREATED` and `ModelEventType.MODEL_DESTROYED` - Add and remove room marker metadata as room models appear or disappear.
 */
export class MarkerModule extends NewModule {
    /**
     * Creates the semi-transparent radar disk mesh used to visualize markers in VR and adds it to the main scene.
     */
    constructor() {
        super("MarkerModule");
        this.markers = new Map();
        this.rooms = new Map();
        this.controllers = new Map();
        this.diskNormal = new Vector3();
        this.currentRoomUID = ROOT_MODEL_UID;
        this.isRadarEnabled = true;
        const sphere = new SphereGeometry(1, 32, 16);
        const material = new MeshBasicMaterial({
            color: 0x6699ff,
            opacity: 0.3,
            transparent: true,
            side: BackSide
        });
        this.radarMesh = new Mesh(sphere, material);
        this.radarMesh.renderOrder = RenderOrder.MARKER_DISK;
        this.radarMesh.matrixAutoUpdate = false;
        this.radarMesh.frustumCulled = false;
        mainScene.add(this.radarMesh);
    }
    /// Life-cycle methods ///
    /**
     * Repositions all room markers every frame: shows them on the controller-anchored VR radar disk if a
     * controller is connected, otherwise anchors them to the desktop screen border.
     */
    onPreRender() {
        const firstControllerIterator = this.controllers.values().next();
        const firstController = firstControllerIterator.done === true ? undefined : firstControllerIterator.value;
        if (firstController !== undefined) {
            this.updateMarkerTransformsForVR(firstController);
        }
        else {
            this.updateMarkerTransformsForDesktop();
        }
    }
    /// Message handlers ///
    onRoomChangedMessage(msg) {
        const roomModel = modelPool.getModel(msg.uid);
        if (roomModel === undefined)
            return; // invalid room UID
        this.currentRoomUID = roomModel.getUID();
        for (const room of this.rooms.values()) {
            room.onCurrentRoomChanged(roomModel.getUID());
        }
        for (const marker of this.markers.values()) {
            marker.onCurrentRoomChanged(this.currentRoomUID);
        }
    }
    onControllerConnectedMessage(msg) {
        const info = getControllerMeshInfo(msg.controllerType, msg.hand).makerRadar;
        this.controllers.set(msg.uid, {
            radarOffset: info.position,
            radarRadius: info.radius,
            radarMarkerScale: info.markerScale,
            radarMarkerOffset: info.radius - 0.5 * info.markerScale,
            pos: new Vector3(),
            ori: new Quaternion()
        });
    }
    onControllerDisconnectedMessage(msg) {
        this.controllers.delete(msg.uid);
    }
    onControllerMovedMessage(msg) {
        const controller = this.controllers.get(msg.uid);
        if (controller !== undefined) {
            controller.pos.fromArray(msg.position);
            controller.ori.fromArray(msg.orientation);
        }
    }
    onModelCreated(model) {
        if (!model.isRoom() || this.rooms.has(model.getUID()))
            return;
        const room = new MarkerRoomInfo(model.getUID(), this.currentRoomUID);
        this.rooms.set(model.getUID(), room);
        for (const marker of this.markers.values()) {
            marker.onRoomLoaded(model);
        }
    }
    onModelDestroyed(model) {
        if (!model.isRoom())
            return;
        const room = this.rooms.get(model.getUID());
        if (room === undefined)
            return;
        this.rooms.delete(model.getUID());
        room.delete();
    }
    onCreateMarkerMessage(msg) {
        const marker = this.markers.get(msg.markerUid);
        if (marker === undefined) {
            this.markers.set(msg.markerUid, new Marker(this.rooms, msg));
        }
        else {
            marker.changeMarkerColor(msg.baseColor, this.currentRoomUID);
        }
    }
    onDeleteMarkerMessage(msg) {
        const marker = this.markers.get(msg.markerUid);
        if (marker !== undefined) {
            marker.delete();
            this.markers.delete(msg.markerUid);
        }
    }
    onUpdateMarkerTargetMessage(msg) {
        const marker = this.markers.get(msg.markerUid);
        if (marker === undefined)
            return;
        const roomModel = modelPool.getModel(msg.roomUid);
        if (roomModel === undefined)
            return;
        marker.updateTarget(msg.position, roomModel.getUID(), this.currentRoomUID);
    }
    onSetClipFadeMessage(msg) {
        const room = this.rooms.get(msg.uid);
        if (room === undefined)
            return;
        room.onSetClipFadeMessage(msg);
    }
    handleEnableMarkerRadarMessage(msg) {
        this.isRadarEnabled = msg.enable;
    }
    /// Marker helper methods ///
    updateMarkerTransformsForDesktop() {
        this.radarMesh.visible = false;
        const dY = Math.tan(0.5 * degToRad(camera.perspectiveCam.fov));
        const dX = dY * camera.perspectiveCam.aspect;
        this.updateMarkerTransforms((marker, target) => {
            marker.positionAtScreenBorder(dX, dY, target);
        });
    }
    updateMarkerTransformsForVR(controller) {
        const platformScale = localPlayerContainer.getPlatformScale();
        this.radarMesh.position.copy(controller.radarOffset).multiplyScalar(platformScale).applyQuaternion(controller.ori).add(controller.pos);
        this.radarMesh.scale.setScalar(platformScale * controller.radarRadius);
        this.diskNormal.subVectors(this.radarMesh.position, localPlayerContainer.cameraWorldPosition).normalize();
        this.radarMesh.quaternion.setFromUnitVectors(tmpVector31.set(0, 0, -1), this.diskNormal);
        this.radarMesh.updateMatrix();
        let numMakersInRadar = 0;
        this.updateMarkerTransforms((marker, target) => {
            marker.positionInRadar(this.radarMesh.position, this.diskNormal, target, controller.radarMarkerOffset, controller.radarMarkerScale);
            ++numMakersInRadar;
        });
        this.radarMesh.visible = numMakersInRadar > 0 && this.isRadarEnabled;
    }
    updateMarkerTransforms(callback) {
        for (const room of this.rooms.values()) {
            room.updateMarkers(this.currentRoomUID, callback);
        }
    }
}
__decorate([
    MessageHandler(RoomChangedMessage)
], MarkerModule.prototype, "onRoomChangedMessage", null);
__decorate([
    MessageHandler(ControllerConnectedMessage)
], MarkerModule.prototype, "onControllerConnectedMessage", null);
__decorate([
    MessageHandler(ControllerDisconnectedMessage)
], MarkerModule.prototype, "onControllerDisconnectedMessage", null);
__decorate([
    MessageHandler(ControllerMovedMessage)
], MarkerModule.prototype, "onControllerMovedMessage", null);
__decorate([
    ModelEventHandler(ModelEventType.MODEL_CREATED)
], MarkerModule.prototype, "onModelCreated", null);
__decorate([
    ModelEventHandler(ModelEventType.MODEL_DESTROYED)
], MarkerModule.prototype, "onModelDestroyed", null);
__decorate([
    MessageHandler(CreateMarkerMessage)
], MarkerModule.prototype, "onCreateMarkerMessage", null);
__decorate([
    MessageHandler(DeleteMarkerMessage)
], MarkerModule.prototype, "onDeleteMarkerMessage", null);
__decorate([
    MessageHandler(UpdateMarkerTargetMessage)
], MarkerModule.prototype, "onUpdateMarkerTargetMessage", null);
__decorate([
    MessageHandler(SetClipFadeMessage)
], MarkerModule.prototype, "onSetClipFadeMessage", null);
__decorate([
    MessageHandler(EnableMarkerRadarMessage)
], MarkerModule.prototype, "handleEnableMarkerRadarMessage", null);
